/** True when Slate/editor HTML is an empty placeholder with no real message text. */
export function isPlaceholderEditorHtml(html: string | null | undefined): boolean {
  if (!html?.trim()) return true;
  if (typeof document === 'undefined') {
    const stripped = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return stripped.length === 0;
  }
  const root = document.createElement('div');
  root.innerHTML = html;
  const text = (root.textContent || root.innerText || '').replace(/\u00a0/g, ' ').trim();
  if (text.length > 0) return false;
  const hasMeaningfulNodes = root.querySelector('img, table, ul, ol, blockquote, hr');
  return !hasMeaningfulNodes;
}

/** Pick the best user-body HTML for persistence (live editor wins when non-placeholder). */
export function resolveUserBodyForPersistence(
  storedUserBody: string,
  liveEditorHtml: string | null | undefined,
): string {
  if (liveEditorHtml != null && !isPlaceholderEditorHtml(liveEditorHtml)) {
    return liveEditorHtml;
  }
  return storedUserBody;
}

function isEmptyComposeParagraphElement(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag !== 'p' && tag !== 'div') return false;
  const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  if (text) return false;
  if (el.querySelector('img, table, ul, ol, blockquote, hr, a[href], pre')) return false;
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      if ((child.textContent ?? '').replace(/\u00a0/g, '').trim()) return false;
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const childTag = (child as Element).tagName.toLowerCase();
    if (childTag !== 'br') return false;
  }
  return true;
}

/** Remove locked quote/raw blocks that must not live in the user typing zone. */
export function stripLockedComposeBlocksFromUserBody(html: string): string {
  if (!html?.trim()) return html;
  if (typeof document === 'undefined') {
    return html
      .replace(/<div[^>]*data-slate-quoted-rich[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div[^>]*data-slate-raw-html[^>]*>[\s\S]*?<\/div>/gi, '');
  }
  const root = document.createElement('div');
  root.innerHTML = html.trim();
  root
    .querySelectorAll('[data-slate-quoted-rich="true"], [data-slate-raw-html="true"]')
    .forEach((el) => el.remove());
  return trimTrailingEmptyComposeParagraphs(root.innerHTML, { maxTrailingEmpty: 0 });
}

/** Remove trailing account signature blocks from compose user HTML. */
export function stripTrailingSignatureBlocks(html: string): string {
  if (!html) return '';
  let out = html.trim();
  let prev = '';
  while (prev !== out) {
    prev = out;
    out = out
      .replace(
        /\s*<div[^>]*\bclass="[^"]*email-signature[^"]*"[^>]*>[\s\S]*?<\/div>\s*$/i,
        '',
      )
      .trim();
    out = out
      .replace(
        /(?:<p[^>]*><br\s*\/?><\/p>\s*)*(?:<p[^>]*>--\s*<\/p>\s*)(?:<p[^>]*><br\s*\/?><\/p>\s*)*$/i,
        '',
      )
      .trim();
  }
  return out;
}

/** Remove reply/forward quoted blocks accidentally saved into a library template body. */
export function stripOutboundQuotedBlocksFromUserHtml(html: string): string {
  if (!html?.trim() || !html.includes('email-quoted-block')) return html;
  if (typeof document === 'undefined') {
    return html.replace(/<blockquote\b[^>]*class="[^"]*email-quoted-block[^"]*"[^>]*>[\s\S]*?<\/blockquote>/gi, '').trim();
  }
  const root = document.createElement('div');
  root.innerHTML = html.trim();
  root.querySelectorAll('blockquote.email-quoted-block').forEach((el) => el.remove());
  return trimTrailingEmptyComposeParagraphs(root.innerHTML, { maxTrailingEmpty: 0 });
}

function countTrailingEmptyParagraphs(root: HTMLElement): number {
  let count = 0;
  for (let i = root.children.length - 1; i >= 0; i--) {
    const el = root.children[i];
    if (!isEmptyComposeParagraphElement(el)) break;
    count++;
  }
  return count;
}

/** Remove excess trailing blank lines before the compose signature / quoted panel. */
export function trimTrailingEmptyComposeParagraphs(
  html: string,
  options?: { maxTrailingEmpty?: number },
): string {
  const maxTrailingEmpty = options?.maxTrailingEmpty ?? 0;
  if (!html?.trim()) return html;
  if (typeof document === 'undefined') {
    let out = html.trim();
    const pattern = /(?:\s*<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/gi;
    while (pattern.test(out) && (out.match(pattern)?.[0].match(/<p\b/gi)?.length ?? 0) > maxTrailingEmpty) {
      out = out.replace(pattern, '').trim();
    }
    return out || '<p><br></p>';
  }
  const root = document.createElement('div');
  root.innerHTML = html.trim();
  let trailing = countTrailingEmptyParagraphs(root);
  while (trailing > maxTrailingEmpty && root.lastElementChild) {
    root.removeChild(root.lastElementChild);
    trailing--;
  }
  const out = root.innerHTML.trim();
  return out || '<p><br></p>';
}
